import { SettlementDetail } from './excel-process';
import { supabase } from '../supabase';
import {
  CostTraceabilityLevel,
  ExtractedSettlementCostLine,
  OfficialCostCategory,
} from './receiver-costs';

export type SettlementCostCategory = OfficialCostCategory;

export type GroupReviewStatus =
  | 'extracted'
  | 'mapped'
  | 'reviewed'
  | 'ready_to_apply';

export type PdfShipmentBoxContext = {
  id?: number | string | null;
  shipment_id?: number | string | null;
  reference_number?: string | null;
  container_number?: string | null;
  receiver?: string | null;
  ship_name?: string | null;
  ship_etd?: string | null;
  ship_eta?: string | null;
  variety?: string | null;
  labeled_variety?: string | null;
  caliber?: string | null;
  color?: string | null;
  category?: string | null;
  box_format?: string | null;
  box_type?: string | null;
  box_label?: string | null;
  box_weight?: number | string | null;
  quantity?: number | string | null;
  pallet?: string | null;
  producer_name?: string | null;
};

export type PdfShipmentContext = {
  reference_number?: string | null;
  container_number?: string | null;
  shipment_id?: number | string | null;
  packingList: PdfShipmentBoxContext[];
};

export type PdfPageRange = {
  start: number;
  end: number;
};

export interface PdfProcessRequest {
  pdfData: string;
  filename: string;
  exporterId: number;
  speciesId?: number;
  shipmentContext?: PdfShipmentContext;
  grouped?: boolean;
  groupedMode?: 'compact' | 'full';
  groupedPageRange?: PdfPageRange;
  model?: string;
}

export interface PdfGroupSalesLine {
  id: string;
  rawText: string;
  variety: string;
  caliber: string;
  color: string;
  label: string;
  format: string;
  quantity: number;
  boxWeight: number;
  kilos: number;
  unitPrice: number;
  amount: number;
  currency: string;
}

export interface PdfGroupCostLine {
  id: string;
  description: string;
  amount: number;
  currency: string;
  mappedCategory: SettlementCostCategory;
  suggestedCategory?: SettlementCostCategory;
  mappingStatus?: 'saved' | 'new';
  mappingId?: string;
  confidence: number;
  sourceLocation?: Record<string, unknown>;
  traceabilityLevel?: CostTraceabilityLevel;
}

export interface PdfSettlementGroup {
  groupId: string;
  accountNumber: string;
  title: string;
  pages: number[];
  status: GroupReviewStatus;
  header: {
    shipName: string;
    voyage: string;
    dateRangeStart: string;
    dateRangeEnd: string;
    clientLot: string;
    brand: string;
    productDescription: string;
    species: string;
    variety: string;
    receiver: string;
    market: string;
    currency: string;
    cases: number;
    kilos: number;
  };
  salesLines: PdfGroupSalesLine[];
  rawSalesLines: string[];
  costLines: PdfGroupCostLine[];
  totals: {
    cases: number;
    kilos: number;
    grossSales: number;
    totalExpenses: number;
    netProceeds: number;
  };
  extractedReferences: string[];
  extractedContainers: string[];
  warnings: string[];
}

export interface PdfProcessResponse {
  success: boolean;
  documentType?: 'single_settlement' | 'multi_group_settlement';
  pageRange?: PdfPageRange;
  data?: SettlementDetail[];
  costLines?: ExtractedSettlementCostLine[];
  groups?: PdfSettlementGroup[];
  warnings?: string[];
  error?: string;
}

export interface PdfGroupedProcessRequest {
  pdfData: string;
  filename: string;
  groupId?: string;
  groupName?: string;
  pageRange?: PdfPageRange;
}

export const processPdfFile = async (
  request: PdfProcessRequest
): Promise<PdfProcessResponse> => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/pdf-process`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(request),
      }
    );

    const responseText = await response.text();
    let data: PdfProcessResponse | { error?: string } = {};

    try {
      data = responseText ? JSON.parse(responseText) : {};
    } catch (_error) {
      data = {
        error:
          responseText || `PDF process failed with status ${response.status}`,
      };
    }

    if (!response.ok) {
      throw new Error(data.error || 'Failed to process PDF file');
    }

    return data as PdfProcessResponse;
  } catch (error) {
    console.error('Error processing PDF file:', error);
    throw error;
  }
};

export const processGroupedPdfFile = async (
  request: PdfGroupedProcessRequest
): Promise<PdfProcessResponse> => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/pdf-process-grouped`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(request),
      }
    );

    const responseText = await response.text();
    let data: PdfProcessResponse | { error?: string } = {};

    try {
      data = responseText ? JSON.parse(responseText) : {};
    } catch (_error) {
      data = {
        error:
          responseText ||
          `Grouped PDF process failed with status ${response.status}`,
      };
    }

    if (!response.ok) {
      throw new Error(data.error || 'Failed to process grouped PDF file');
    }

    return data as PdfProcessResponse;
  } catch (error) {
    console.error('Error processing grouped PDF file:', error);
    throw error;
  }
};
